import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  ContentCopy,
  ExpandMore,
  PlayArrow,
  Pause,
  CheckCircle,
  Warning,
  Refresh,
  MonitorHeart,
  DeleteOutline,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { usePlayerHealthDevice, usePlayerEvents, useRemovePlayerDevice } from '../../hooks/useFleet';
import type { PlayerHealthDevice, PlayerTelemetryEvent } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { fleetHelp } from '../../components/PageHelp/helpContent';
import { FLEET_TABS, FLEET_ACCENT_COLOR } from './fleetTabs';
import {
  PLAYER_STATUS_COLORS,
  PLAYER_STATUS_LABELS,
  EVENT_TYPE_COLORS,
  formatSecondsAgo,
  formatDuration,
  formatTrackPosition,
  formatTimestamp,
  trackLabel,
  deviceLabel,
  describeFailure,
  eventSummary,
  errorToMarkdown,
} from './playerHealthFormat';

// Option values are passed straight to the API's ?type= filter, which
// accepts a comma-separated list; "Errors" covers both error kinds.
const ERRORS_FILTER = 'error,audio_error';
const EVENT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All events' },
  { value: ERRORS_FILTER, label: 'Errors' },
  { value: 'audio_error', label: 'Audio errors only' },
  { value: 'state_change', label: 'Play / track changes' },
  { value: 'lifecycle', label: 'Lifecycle' },
];

const SECTION_SX = { bgcolor: '#1e1e1e', borderRadius: 2, p: 3, mb: 3 } as const;

function yesNo(value: boolean | null): string {
  if (value === null) return '-';
  return value ? 'Yes' : 'No';
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-word' }}
      >
        {value === null || value === undefined || value === '' ? '-' : value}
      </Typography>
    </Box>
  );
}

function isErrorEvent(event: PlayerTelemetryEvent): boolean {
  return event.event_type === 'error' || event.event_type === 'audio_error';
}

function EventRow({
  event,
  device,
  onCopy,
}: {
  event: PlayerTelemetryEvent;
  device: PlayerHealthDevice;
  onCopy: (text: string) => void;
}) {
  const color = EVENT_TYPE_COLORS[event.event_type] || '#6b7280';
  const summary = eventSummary(event);
  const data = event.event_data || {};
  const clientTime = typeof data.client_time === 'number' ? new Date(data.client_time).toLocaleString() : null;

  if (!isErrorEvent(event)) {
    return (
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', py: 1 }}>
        <Chip
          label={event.event_type.replace('_', ' ')}
          size="small"
          sx={{ bgcolor: `${color}20`, color, minWidth: 110, justifyContent: 'center' }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2">{summary}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(event.created_at)}
            {clientTime && clientTime !== formatTimestamp(event.created_at) ? ` (client ${clientTime})` : ''}
            {event.app_version ? ` / ${event.app_version.slice(0, 7)}` : ''}
          </Typography>
        </Box>
      </Box>
    );
  }

  const { error_kind, message, stack, error_name, client_time, ...context } = data;
  const hasContext = Object.keys(context).length > 0;

  return (
    <Accordion disableGutters sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', pr: 1 }}>
          <Chip
            label={typeof error_kind === 'string' ? error_kind : event.event_type}
            size="small"
            sx={{ bgcolor: `${color}20`, color, minWidth: 110, justifyContent: 'center' }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {typeof message === 'string' && message ? message : summary}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTimestamp(event.created_at)}
              {clientTime ? ` (client ${clientTime})` : ''}
              {event.app_version ? ` / ${event.app_version.slice(0, 7)}` : ''}
              {typeof error_name === 'string' ? ` / ${error_name}` : ''}
            </Typography>
          </Box>
          <Tooltip title="Copy as markdown for triage">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onCopy(errorToMarkdown(event, device));
              }}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        {typeof stack === 'string' && stack && (
          <Box sx={{ mb: hasContext ? 2 : 0 }}>
            <Typography variant="caption" color="text.secondary">
              Stack
            </Typography>
            <Box
              component="pre"
              sx={{ m: 0, p: 1.5, bgcolor: '#111', borderRadius: 1, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap' }}
            >
              {stack}
            </Box>
          </Box>
        )}
        {hasContext && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Context
            </Typography>
            <Box
              component="pre"
              sx={{ m: 0, p: 1.5, bgcolor: '#111', borderRadius: 1, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap' }}
            >
              {JSON.stringify(context, null, 2)}
            </Box>
          </Box>
        )}
        {!stack && !hasContext && (
          <Typography variant="body2" color="text.secondary">
            No stack or context captured for this error.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default function PlayerDeviceDetail() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?events=error (from the list page's error count) lands on the Errors filter.
  const initialEvents = searchParams.get('events');
  const [eventType, setEventType] = useState<string>(
    initialEvents === 'error' || initialEvents === 'errors'
      ? ERRORS_FILTER
      : EVENT_TYPE_OPTIONS.some((o) => o.value === initialEvents)
      ? (initialEvents as string)
      : ''
  );
  const [copied, setCopied] = useState(false);

  const id = deviceId || '';
  const { data: device, isLoading, error, isFetching, dataUpdatedAt } = usePlayerHealthDevice(id);
  const removeDevice = useRemovePlayerDevice();

  const handleRemove = async () => {
    if (!device) return;
    const label = deviceLabel(device);
    if (!window.confirm(`Remove player "${label}" (${device.user_email || 'unknown user'})? Its history is deleted. If the install reports again it will re-register.`)) return;
    try {
      await removeDevice.mutateAsync(device.device_id);
      navigate('/fleet/player-health');
    } catch (err) {
      console.error('Failed to remove player device:', err);
      window.alert(`Failed to remove player: ${(err as Error)?.message || 'Unknown error'}`);
    }
  };
  const events = usePlayerEvents(id, { type: eventType || undefined, limit: 50 });

  const eventRows = useMemo(
    () => (events.data?.pages || []).flatMap((p) => p.data),
    [events.data]
  );

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (err) {
      console.error('Clipboard write failed:', err);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress sx={{ color: FLEET_ACCENT_COLOR }} />
      </Box>
    );
  }

  if (error || !device) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load this player. It may not exist or may belong to another organization.</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/fleet/player-health')} sx={{ mt: 2 }}>
          Back to Player Health
        </Button>
      </Box>
    );
  }

  const colors = PLAYER_STATUS_COLORS[device.status];
  const track = trackLabel(device);
  const updatedSecondsAgo = dataUpdatedAt ? Math.round((Date.now() - dataUpdatedAt) / 1000) : null;

  return (
    <Box>
      <SectionTabs
        title="Fleet Management"
        tabs={FLEET_TABS}
        accentColor={FLEET_ACCENT_COLOR}
        help={fleetHelp.playerHealth}
      />

      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/fleet/player-health" color="inherit" sx={{ textDecoration: 'none' }}>
          Player Health
        </Link>
        <Typography color="text.primary">{deviceLabel(device)}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/fleet/player-health')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <MonitorHeart sx={{ color: FLEET_ACCENT_COLOR }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {deviceLabel(device)}
              </Typography>
              <Chip
                label={PLAYER_STATUS_LABELS[device.status]}
                size="small"
                sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 600 }}
              />
              {!device.live && (
                <Chip label="No live snapshot" size="small" variant="outlined" />
              )}
              {device.meets_requirements === false && (
                <Tooltip
                  title={
                    <Box>
                      {device.requirement_failures.map((f) => (
                        <div key={f}>{describeFailure(f)}</div>
                      ))}
                    </Box>
                  }
                >
                  <Chip icon={<Warning />} label="Below requirements" size="small" sx={{ bgcolor: '#f59e0b20', color: '#f59e0b' }} />
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {device.device_id}
              </Typography>
              <Tooltip title="Copy device id">
                <IconButton size="small" onClick={() => copyText(device.device_id)}>
                  <ContentCopy sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Last seen {formatSecondsAgo(device.last_seen_seconds_ago).toLowerCase()}
            {' / '}
            {isFetching ? 'refreshing...' : updatedSecondsAgo !== null ? `checked ${formatSecondsAgo(updatedSecondsAgo).toLowerCase()}` : ''}
            {' '}(auto-refresh 30s)
          </Typography>
          <Tooltip title="Delete this install and its history. It re-registers on its next heartbeat if it is still in use.">
            <span>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteOutline />}
                onClick={handleRemove}
                disabled={removeDevice.isPending}
              >
                Remove Player
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={SECTION_SX}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Live Snapshot
        </Typography>
        {device.live ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
            <Field
              label="Playback"
              value={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {device.is_playing ? (
                    <PlayArrow fontSize="small" sx={{ color: '#10b981' }} />
                  ) : (
                    <Pause fontSize="small" sx={{ color: '#6b7280' }} />
                  )}
                  {device.is_playing ? 'Playing' : 'Paused'}
                </Box>
              }
            />
            <Field
              label="Current track"
              value={
                track
                  ? `${track}${device.current_track_position !== null ? ` (${formatTrackPosition(device.current_track_position)})` : ''}`
                  : '-'
              }
            />
            <Field label="Audio context" value={device.audio_context_state} />
            <Field label="Page visible" value={yesNo(device.page_visible)} />
            <Field label="Online" value={yesNo(device.online)} />
            <Field label="Display mode" value={device.display_mode} />
            <Field label="Engine" value={device.engine} />
            <Field label="Build" value={device.app_version} mono />
            <Field
              label="Requirements"
              value={
                device.meets_requirements === null ? (
                  '-'
                ) : device.meets_requirements ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircle fontSize="small" sx={{ color: '#10b981' }} /> Met
                  </Box>
                ) : (
                  <Box>
                    {device.requirement_failures.map((f) => (
                      <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Warning fontSize="small" sx={{ color: '#f59e0b' }} /> {describeFailure(f)}
                      </Box>
                    ))}
                  </Box>
                )
              }
            />
            <Field label="Session started" value={formatTimestamp(device.session_started_at)} />
            <Field label="Session duration" value={formatDuration(device.session_duration_seconds)} />
            <Field label="Tracks this session" value={device.tracks_played_count} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            This player has not sent a heartbeat in the last 5 minutes, so there is no live state to show.
            The registry details below reflect its last report.
          </Typography>
        )}
      </Paper>

      <Paper sx={SECTION_SX}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Device
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
          <Field
            label="User"
            value={
              device.user_email
                ? `${device.user_email}${device.user_name && device.user_name !== device.organization_name ? ` (${device.user_name})` : ''}`
                : '-'
            }
          />
          <Field label="Organization" value={device.organization_name} />
          <Field label="Site" value={device.site_name} />
          <Field label="Zone" value={device.zone_name} />
          <Field label="First seen" value={formatTimestamp(device.first_seen_at)} />
          <Field label="Last seen" value={formatTimestamp(device.last_seen_at)} />
          <Field label="Last known build" value={device.app_version} mono />
          <Field label="Display mode" value={device.display_mode} />
          <Field label="Heartbeats (lifetime)" value={device.total_heartbeats} />
          <Field label="Errors (lifetime)" value={device.total_errors} />
          <Field label="Errors (24h)" value={device.errors_last_24h} />
          <Box sx={{ gridColumn: { xs: 'auto', md: 'span 4' } }}>
            <Field label="User agent" value={device.user_agent} mono />
          </Box>
        </Box>
      </Paper>

      <Paper sx={SECTION_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Event Timeline
          </Typography>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={eventType}
              label="Type"
              onChange={(e) => setEventType(e.target.value)}
            >
              {EVENT_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={() => events.refetch()}
            disabled={events.isFetching}
          >
            Refresh
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Newest first. Heartbeats are not logged; only state changes, lifecycle markers and errors. Events are kept for 7 days.
        </Typography>
        <Divider sx={{ mb: 1 }} />

        {events.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : events.error ? (
          <Alert severity="error">Failed to load events: {(events.error as Error)?.message || 'Unknown error'}</Alert>
        ) : eventRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No events recorded{eventType ? ' for this type' : ''} in the last 7 days.
          </Typography>
        ) : (
          <Box>
            {eventRows.map((event) => (
              <Box key={event.id} sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <EventRow event={event} device={device} onCopy={copyText} />
              </Box>
            ))}
            {events.hasNextPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Button
                  size="small"
                  onClick={() => events.fetchNextPage()}
                  disabled={events.isFetchingNextPage}
                >
                  {events.isFetchingNextPage ? 'Loading...' : 'Load older events'}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Copied to clipboard"
      />
    </Box>
  );
}
